import {
  AssetType,
  CMS,
  MarketPlace,
  SelectionType,
  TransformationVariant,
} from '@amzn/fire-tv-campaign-automation-service';

import placeholderBgImage1920x1080 from './images/1920_1080_background_image.png';
import placeholderBgImage from './images/placeholder_background_image.jpg';
import placeholderLogoImage from './images/placeholder_logo_image.png';
import placeholderVideoTrailerImage from './images/placeholder_video_trailer_image.jpg';

const DEFAULT_LOCALE = 'en-US';
const DEFAULT_SUPPORT_EMAIL = 'selvankr-staff@amazon.com';

/**
 * All the constants under this namespace are provisioned via manifest templating.
 *
 * @see {@link https://katal.amazon.dev/build-and-deploy/tutorials/deployment-manifest-templating/ | Docs}
 */
namespace ProvisionedConstants {
  /**
   * The cross account role at the control plane account that allows
   * the application to access the control plane.
   */
  export const CONTROL_PLANE_CROSS_ACCOUNT_ROLE_ARN: string = 'arn:aws:iam::561340725304:role/Manual-FridaCrossAccountRole-us-west-2';
  /**
   * The endpoint URL of the control plane.
   */
  export const CONTROL_PLANE_ENDPOINT: string = 'https://lgauuo22kd.execute-api.us-east-1.amazonaws.com/dev/';
  /**
   * The AWS region of the control plane.
   */
  export const CONTROL_PLANE_REGION: string = 'us-east-1';
  /**
   * The Cognito Identity Pool ID.
   */
  export const IDENTITY_POOL_ID: string = 'us-west-2:c4993bea-b56c-4b14-8d9f-df270774ddc2';
  export const KATAL_LOGGER_URL: string = 'https://log.sruthivs.us-west-2.dev.fridacentral.ftvcampaigns.amazon.dev/v1/log';
  /**
   * The Cognito User Pool Client ID.
   */
  export const USER_POOL_CLIENT_ID: string = '2tspm94mb3cd940shhq63kp1jg';
  /**
   * The domain name of the Cognito User Pool.
   */
  export const USER_POOL_DOMAIN: string = 'sruthivs-dev-us-west-2-frida-central';
  /**
   * The Cognito User Pool ID.
   */
  export const USER_POOL_ID: string = 'us-west-2_SBmM6VMFc';
  /**
   * The AWS region of the Cognito User Pool.
   */
  export const USER_POOL_REGION: string = 'us-west-2';
  /**
   * The AWS region of the application.
   */
  export const STAGE: string = 'Dev';
}

namespace AuthConstants {
  export const POST_AUTH_REDIRECT = 'postAuthRedirect';
}

namespace HttpConstants {
  export const HEADERS = {
    CONTENT_TYPE: 'content-type',
    CONTENT_LENGTH: 'content-length',
  } as const;
  export const PARSE = {
    RADIX: 10, // For parsing Content-Length
  } as const;
}

export const LOGGING_TIMEOUT_MS = 1000;
namespace ErrorConstants {
  export const NAMES = {
    INVALID_INPUT: 'InvalidInputException',
    VALIDATION: 'ValidationError',
    POLLING_ERROR: 'PollingError',
    POLLING_INPUT_ERROR: 'PollingInputError',
    POLLING_ABORTED_ERROR: 'PollingAbortedError',
    POLLING_TIMEOUT_ERROR: 'PollingTimeoutError',
    POLLING_MAX_RETRIES_ERROR: 'PollingMaxRetriesError',
    POLLING_SUCCESS_CONDITION_ERROR: 'PollingSuccessConditionError',
    ASSET_UPLOAD_ERROR: 'AssetUploadError',
  } as const;
  export const MESSAGES = {
    // User-facing message
    CANVAS_CONTEXT_NOT_FOUND: 'Could not get canvas context',
    FAILED_TO_GENERATE_PREVIEW: 'Failed to generate preview',
    FAILED_TO_UPDATE_PREVIEW_ASSETS: 'Failed to update preview assets',
    GENERAL_IMAGE_VALIDATION_ERROR: 'Error while validating baseline image',
    INVALID_BASELINE_JPEG: 'Invalid JPEG: Please upload a Baseline JPEG image',
    INVALID_FILE_TYPE: 'Invalid File Type: File is not a image',
    INVALID_JPEG_TYPE: 'Invalid JPEG: File is not a JPEG image',
    INVALID_PREVIEW_STATE: 'Invalid preview state',
    FAILED_TO_UPLOAD: 'Failed to upload file',
    FAILED_TO_GET_PRESIGNED_URL: 'Failed to get presigned URL',
    CONTENT_TYPE_HEADER_MISSING: 'Content-Type header is missing',
    CONTENT_LENGTH_HEADER_MISSING: 'Content-Length header is missing',
    UNEXPECTED_VALIDATION_ERROR: 'An unexpected error occurred during validation',
    TRY_WITH_DIFFERENT_TRANSFORMATION: 'Please try different Transformation Template',
    INVALID_ASPECT_RATIO: 'Provided asset does not match required aspect ratio',
    TRANSFORMATION_ERROR: 'Error in transforming the asset',
    HTTP_ERROR: 'HTTP ERROR',

    // Internal diagnostic messages
    ERROR_UPDATING_PRESIGNED_URLS: 'Error updating presigned URLs',
    INCOMPLETE_MARKER: 'Invalid JPEG: Incomplete marker',
    INCOMPLETE_SEGMENT: 'Invalid JPEG: Incomplete segment',
    INVALID_JPEG_HEADER: 'Invalid JPEG: File header is corrupted',
    INVALID_MARKER: 'Invalid JPEG: Invalid marker',
    INVALID_SEGMENT_LENGTH: 'Invalid JPEG: Invalid segment length',
    NO_SOF_MARKER: 'Invalid JPEG: No encoding type marker found',
    FILE_ATTACHMENT_ERROR: 'File attachment error',
    PLACEHOLDER_URL_NOT_DEFINED: 'Placeholder URL not defined for asset type',
    PROGRESSIVE_JPEG_ERROR: 'Progressive Image is detected',
    UPLOAD_FAILED_WITH_STATUS: 'Upload failed with status',
    LOGO_TITLE_EMPTY: 'Logo Title cannot be empty',
    LOGO_DESCRIPTION_EMPTY: 'Logo Description cannot be empty',
    CTA_REQUIRED: 'Please select a Call To Action',
    PROVIDER_LOGO_REQUIRED: 'Please select a Provider Logo',
    UNSAVED_CHANGES: 'Unsaved changes',
  } as const;
}

namespace MediaConstants {
  export namespace JPEG {
    export const SIGNATURE = 0xffd8; // Start of Image marker (SOI)
    export const MARKER = {
      START: 0xff, // Marker prefix
      SOF0: 0xc0, // Baseline marker
      SOF2: 0xc2, // Progressive marker
      EOI: 0xd9, // End of Image marker
      FE: 0xfe, // Comment or extension marker
      RST: {
        START: 0xd0,
        END: 0xd7,
      },
    };
    export const MIME_REGEX = /^image\/jpe?g$/i;
  }

  export namespace ELEMENTS {
    export const VIDEO = 'video' as const;
  }

  export namespace VIDEO {
    export const DEFAULT_PRELOAD = 'metadata' as const;
  }

  export namespace MIME {
    export const PREFIXES = {
      IMAGE: 'image/',
      VIDEO: 'video/',
    } as const;
  }

  export const TIMEOUTS = {
    MEDIA_IMAGE_LOAD: 30000, // 30 seconds for loading media image files
    MEDIA_VIDEO_LOAD: 45000, // 45 seconds for loading videos files
  } as const;

  export const VALIDATION = {
    VIDEO_DURATION_PRECISION_TOLERANCE: 0.05, // 50ms tolerance for metadata parsing differences
  } as const;
}

namespace AssetConstants {
  export const MAX_FILENAME_LENGTH = 100;
  export const DEFAULT_ASPECT_RATIO_TOLERANCE = 0.01;
  export const SEARCH_QUERY_MAX_LENGTH = 100;

  // Transformation variants that don't require file assets (no validation needed)
  export const NO_ASSET_VARIANTS: TransformationVariant[] = [
    TransformationVariant.TEXT_BASED_TITLE_DESCRIPTION,
    TransformationVariant.TEXT_BASED_TITLE_FIRE_TABLETS,
  ];

  export const VIDEO_DURATION = {
    DEFAULT_START_TIME: 0,
    DEFAULT_END_TIME: 21,
    MIN_DURATION: 5,
    MAX_DURATION: 21,
  } as const;

  export const CROP = {
    FINAL_OUTPUT_HEIGHT: 1080,
    KEPT_HEIGHT: 720,
    FINAL_OUTPUT_WIDTH: 1920,
    KEPT_WIDTH: 1440,
    RESIZE_DEBOUNCE_MS: 100,
  } as const;
}

namespace PreviewConstants {
  export const PREVIEW_LABELS = {
    SELECTED: 'Selected',
    UNSELECTED: 'Unselected',
    VIDEO: 'Video',
  } as const;
  export const PREVIEW_ALT_TEXT = {
    Selected: 'Please upload both background image and logo to preview Selected State',
    Unselected: 'Please upload both background image and logo to preview Unselected State',
    Video: 'Please upload a video file to preview video trailer',
  };
  export const COMPONENT_NAMES = {
    PREVIEW_SECTION: 'PreviewSection',
  };
  export const CANVAS = {
    ELEMENT: 'canvas',
    CONTEXT: '2d',
    CROSS_ORIGIN: 'anonymous',
    IMAGE_FORMAT: 'image/jpeg',
    IMAGE_QUALITY: 0.95,
  } as const;

  export const LOADING_PREVIEW_TEXT = 'Loading preview...';

  export const CANVAS_UTILS = {
    DEFAULT_SHIFT_FACTOR: 0.5,
  } as const;
}

namespace StateConstants {
  export const EMPTY_STRING = '';
  export const INPUT_DEBOUNCE_MS = 1000;
}

/**
 * Download-related constants
 */
export const DownloadConstants = {
  LABELS: {
    DOWNLOAD_ASSETS: 'Download Assets',
    DOWNLOAD_ALL: 'Download All',
  },
  ASSET_TYPE_LABELS: {
    [AssetType.BACKGROUND_IMAGE]: 'Background Image',
    [AssetType.LOGO]: 'Logo',
    [AssetType.VIDEO_TRAILER]: 'Video',
  } as Record<AssetType, string>,
  ASSET_TYPE_EXTENSIONS: {
    [AssetType.BACKGROUND_IMAGE]: 'jpg',
    [AssetType.LOGO]: 'png',
    [AssetType.VIDEO_TRAILER]: 'mp4',
  } as Record<AssetType, string>,
  ZIP_FILENAME_PREFIX: 'campaign_assets',
  DOWNLOAD_ALL_VALUE: 'download-all',
  TIMEOUT_MS: 30000, // 30 seconds
  DOWNLOAD_FAILED: 'Download failed. Please try again.',
  PREVIEW_FILE_PREFIX: 'preview',
  PREVIEW_FILE_EXTENSION: '.png',
} as const;

// ToDo: Add a translation bundle for campaign automation and move these to
// the puff.json
namespace TranslatableConstants {
  export const INPUT_TYPE_CONFIRMATION_MESSAGE =
    'Changing the input will clear your current input. Are you sure you want to continue?';
  export const URL_CONFIRMATION_MESSAGE =
    'Fetching a new URL will replace your current input. Are you sure you want to continue?';
  export const URL_CLEAR_CONFIRMATION_MESSAGE =
    'Clearing this URL will remove the associated input. Are you sure you want to continue?';
  export const CATALOG_SELECTION_CONFIRMATION_MESSAGE =
    'Selecting a new image will replace your current selection. Are you sure you want to continue?';
  export const CATALOG_SELECTION_CLEAR_CONFIRMATION_MESSAGE =
    'Clearing this selection will remove the selected image. Are you sure you want to continue?';
  export const CAMPAIGN_CREATION_SUCCESS = 'Campaign Created Successfully!';
  export const CAMPAIGN_SUCCESS_FOLLOW_UP =
    'Your campaign has been created successfully. You can access it using the URL below:';
  export const CONFIRM_VIEW_CAMPAIGN = 'Would you like to view the Campaign?';
  export const CONFIRM_DIALOG_CLOSE_MESSAGE = (cms: CMS) =>
    `You can always go to ${cms} to find the created campaigns by user alias`;
  export const CONFIRM_TEXT = 'Ok';
  export const CANCEL_TEXT = 'No, go back';
  export const CA_MARKETPLACE_DISCLAIMER =
    'CA marketplace requires both en-CA and fr-CA. Please create separate campaigns.';
}

namespace TimeZoneConstants {
  /**
   * Reference: https://data.iana.org/time-zones/tzdb-2021a/zone1970.tab
   */
  export const ALLOWED_TIMEZONES = [
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Berlin',
    'Europe/Paris',
    'Europe/Rome',
    'Europe/Madrid',
    'Asia/Tokyo',
    'America/Vancouver',
    'America/Mexico_City',
    'Asia/Kolkata',
    'UTC',
  ] as const;
  export type AllowedTimezone = (typeof ALLOWED_TIMEZONES)[number];
  export const MARKETPLACE_TO_TZ_MAPPING: Partial<
    Record<MarketPlace, { timeZone: AllowedTimezone; startTime: string; endTime: string }>
  > = {
    IN: {
      timeZone: 'Asia/Kolkata',
      startTime: '00:00',
      endTime: '23:59',
    },
    JP: {
      timeZone: 'Asia/Tokyo',
      startTime: '10:00',
      endTime: '09:59',
    },
    US: {
      timeZone: 'America/Los_Angeles',
      startTime: '21:00',
      endTime: '20:59',
    },
    CA: {
      timeZone: 'America/Vancouver',
      startTime: '00:00',
      endTime: '23:59',
    },
    MX: {
      timeZone: 'America/Mexico_City',
      startTime: '00:00',
      endTime: '23:59',
    },
    UK: {
      timeZone: 'Europe/London',
      startTime: '00:00',
      endTime: '23:59',
    },
    DE: {
      timeZone: 'Europe/Berlin',
      startTime: '00:00',
      endTime: '23:59',
    },
    FR: {
      timeZone: 'Europe/Paris',
      startTime: '00:00',
      endTime: '23:59',
    },
    IT: {
      timeZone: 'Europe/Rome',
      startTime: '00:00',
      endTime: '23:59',
    },
    ES: {
      timeZone: 'Europe/Madrid',
      startTime: '00:00',
      endTime: '23:59',
    },
  };
}

namespace KatalErrorConstants {
  export const KATAL_MAX_SIZE_ERROR = 'kat_over_max_size';
  export const KATAL_UNSUPPORTED_FILE_ERROR = 'kat_unsupported_file_attached';
}

// Default placeholder URLs for missing assets
const PLACEHOLDER_URLS: Record<AssetType, string> = {
  [AssetType.BACKGROUND_IMAGE]: placeholderBgImage,
  [AssetType.LOGO]: placeholderLogoImage,
  [AssetType.VIDEO_TRAILER]: placeholderVideoTrailerImage,
} as const;

// Placeholder URLs for Screen Saver (1920x1080)
const SCREEN_SAVER_PLACEHOLDER_URLS: Partial<Record<AssetType, string>> = {
  [AssetType.BACKGROUND_IMAGE]: placeholderBgImage1920x1080,
  [AssetType.LOGO]: placeholderLogoImage,
} as const;

const CONFIRMATION_CONTEXT = {
  INPUT_TYPE: 'input-type',
  URL: 'url',
  URL_CLEAR: 'url-clear',
  CATALOG_SELECTION: 'catalog-selection',
  CATALOG_SELECTION_CLEAR: 'catalog-selection-clear',
} as const;

enum Poller {
  Transformation,
  Campaign,
}

const TEXT_BASED_TITLE_DESCRIPTION_LENGTH = {
  LOGO_TITLE_MAX_LENGTH_WITH_DESCRIPTION: 35,
  LOGO_TITLE_MAX_LENGTH_WITHOUT_DESCRIPTION: 70,
  LOGO_DESCRIPTION_MAX_LENGTH: 50,
  CTA_MAX_LENGTH_WITH_LOGO: 25,
  CTA_MAX_LENGTH_NO_LOGO: 35,
};

export enum STAGE {
  DEV = 'Dev', // For personal stack and local testing
  BETA = 'Beta',
  GAMMA = 'Gamma',
  PROD_NA = 'Prod-NA',
  PROD_FE = 'Prod-FE',
}

export const NAMESPACE = 'FridaCentralWebsite';

namespace WeblabConstants {
  export const WEBLAB_ID_MAX_LENGTH = 50;
  export const DEFAULT_HEADER_TEXT = 'Weblab Segments:';
  export const BUTTON_TEXT = {
    ADD_WEBLAB: 'Add WebLab',
  } as const;
  export const ERROR_MESSAGES = {
    WEBLAB_ID_REQUIRED: 'Weblab ID is required',
    WEBLAB_ID_ALREADY_EXISTS: 'Weblab ID already exists',
    INVALID_WEBLAB_SEGMENTS: 'Invalid weblab segments provided. Please check the data.',
  } as const;
  export const TREATMENT_OPTIONS = Array.from({ length: 21 }, (_, i) => ({
    name: i === 0 ? 'C' : `T${i}`,
    value: i === 0 ? 'C' : `T${i}`,
  }));
  export const SELECTION_OPTIONS = [
    { name: 'Include', value: SelectionType.INCLUDE },
    { name: 'Exclude', value: SelectionType.EXCLUDE },
  ];
}

export {
  Poller,
  DEFAULT_LOCALE,
  DEFAULT_SUPPORT_EMAIL,
  AssetConstants,
  AuthConstants,
  HttpConstants,
  ErrorConstants,
  MediaConstants,
  PreviewConstants,
  ProvisionedConstants,
  StateConstants,
  TranslatableConstants,
  TimeZoneConstants,
  KatalErrorConstants,
  WeblabConstants,
  PLACEHOLDER_URLS,
  SCREEN_SAVER_PLACEHOLDER_URLS,
  CONFIRMATION_CONTEXT,
  TEXT_BASED_TITLE_DESCRIPTION_LENGTH,
};
